import React, { useEffect, useRef, useState } from "react";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf";
import "pdfjs-dist/web/pdf_viewer.css";

// Set the worker source (needed for pdf.js to function).
GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js`;

const styles = {
    container: {
        height: "100vh",
        width: "100vw",
        position: "relative",
        overflow: "auto",
    },
    canvas: {
        display: "block",
        margin: "0 auto",
        width: "100%",
        height: "auto",
    },
    highlight: {
        position: "absolute",
        backgroundColor: "rgba(255, 255, 0, 0.4)",
        pointerEvents: "none",
    },
};

const PdfViewer = ({ pdfUrl, pageNumber, highlightAreas }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [pdfDocument, setPdfDocument] = useState(null);
    const renderTaskRef = useRef(null); // Ref to store the render task

    useEffect(() => {
        const loadPdf = async () => {
            const loadingTask = getDocument(pdfUrl);
            const pdf = await loadingTask.promise;
            setPdfDocument(pdf);
        };

        loadPdf();
    }, [pdfUrl]);

    const renderPage = async (pageNumber) => {
        if (!pdfDocument || !canvasRef.current) return;

        const page = await pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1.5 });

        // Resize canvas to fit the page.
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
            canvasContext: context,
            viewport,
        };

        // Cancel any ongoing rendering task.
        if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
        }

        // Start a new rendering task.
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        try {
            await renderTask.promise; // Wait for the rendering to complete.

            // Adjust container size for absolute positioning.
            if (containerRef.current) {
                containerRef.current.style.width = `${viewport.width}px`;
                containerRef.current.style.height = `${viewport.height}px`;
            }

            // Scale highlights to match the viewport.
            const scaledHighlights = highlightAreas.map((highlight) => ({
                left: highlight.left * viewport.scale,
                top: highlight.top * viewport.scale,
                width: highlight.width * viewport.scale,
                height: highlight.height * viewport.scale,
            }));

            setScaledHighlights(scaledHighlights);
        } catch (error) {
            if (error.name !== "RenderingCancelledException") {
                console.error("Error during rendering:", error);
            }
        }
    };

    const [scaledHighlights, setScaledHighlights] = useState([]);

    useEffect(() => {
        if (pdfDocument) {
            renderPage(pageNumber); // Render the specified page.
        }

        // Cleanup to cancel rendering task when unmounting or re-rendering.
        return () => {
            if (renderTaskRef.current) {
                renderTaskRef.current.cancel();
            }
        };
    }, [pdfDocument, pageNumber, highlightAreas]);

    return (
        <div style={styles.container} ref={containerRef}>
            <canvas ref={canvasRef} style={styles.canvas} />
            {scaledHighlights.map((highlight, index) => (
                <div
                    key={index}
                    style={{
                        ...styles.highlight,
                        left: highlight.left,
                        top: highlight.top,
                        width: highlight.width,
                        height: highlight.height,
                    }}
                />
            ))}
        </div>
    );
};


export default PdfViewer;
